import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { supabase } from './services/supabase/supabaseClient';
import { loginSuccess } from './redux/authSlice';

import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import RegisterMemberPage from './pages/RegisterMemberPage';
import QuestionerPage from './pages/QuestionerPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MemberListPage from './pages/MemberListPage';
import EditMemberPage from './pages/EditMemberPage';
import ProfilePage from './pages/ProfilePage';
import SkillPage from './pages/SkillPage';
import ApprovalPage from './pages/ApprovalPage';
import DonationPage from './pages/DonationPage';
import EventAttendancePage from './pages/EventAttendancePage';
import Footer from './components/Footer';

import { LoadingProvider } from './components/LoadingContext';

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);

  // Ambil member data dari Supabase
  const fetchMemberData = async (authUserId) => {
    const { data, error } = await supabase
      .from('members')
      .select('id, member_id, email, role_id, is_active, full_name')
      .eq('auth_id', authUserId)
      .single();
    if (error || !data) return null;
    return data;
  };

  // Restore session jika ada (refresh / reload)
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;

        const session = data.session;
        if (session?.user) {
          const memberData = await fetchMemberData(session.user.id);
          if (memberData) {
            dispatch(loginSuccess(memberData));
          }
        }
      } catch (err) {
        console.error('Restore session error:', err);
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, [dispatch]);

  if (loading) return <div>Loading...</div>;

  return (
    <LoadingProvider>
      <Router>
        <Routes>
          {/* Root / → redirect sesuai login state */}
          <Route
            path="/"
            element={user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />}
          />

          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register/:id" element={<RegisterMemberPage />} />
          <Route path="/questioner" element={<QuestionerPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected Routes */}
          <Route
            path="/"
            element={user ? <Layout menuOpen={menuOpen} setMenuOpen={setMenuOpen} /> : <Navigate to="/login" replace />}
          >
            <Route path="dashboard" element={<DashboardPage />} />
            {user && <Route path="members" element={[1, 2].includes(user.role_id) ? <MemberListPage /> : <Navigate to="/dashboard" />} />}
            <Route path="edit-member/:id" element={<EditMemberPage />} />
            <Route path="profile/:id" element={<ProfilePage />} />
            {user && <Route path="skills" element={[1, 2].includes(user.role_id) ? <SkillPage /> : <Navigate to="/dashboard" />} />}
            {user && <Route path="approval" element={[1, 2].includes(user.role_id) ? <ApprovalPage /> : <Navigate to="/dashboard" />} />}
            {user && <Route path="donation" element={[1, 2, 5].includes(user.role_id) ? <DonationPage /> : <Navigate to="/dashboard" />} />}
            {user && <Route path="event-attendance" element={[1, 2, 4].includes(user.role_id) ? <EventAttendancePage /> : <Navigate to="/dashboard" />} />}
          </Route>
        </Routes>
        <Footer />
      </Router>
    </LoadingProvider>
  );
}

export default App;
